import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const scrollToSection = useCallback((id: string) => {
    setActiveSection(id);
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
      return;
    }

    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
    setIsMobileMenuOpen(false);
  }, [location.pathname, navigate]);

  const getButtonClass = (sectionId: string) => {
    const baseClass = "transition-colors duration-300";
    const isActive = activeSection === sectionId;
    
    return `${baseClass} ${
      isActive 
        ? 'text-amber-600 dark:text-amber-500 font-bold border-b-2 border-amber-500 dark:border-amber-500' 
        : 'text-gray-700 dark:text-gray-300 hover:text-amber-600 dark:hover:text-amber-500'
    }`;
  };

  const handleLogoClick = () => {
    setActiveSection('');
    navigate('/');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-md ${
        isScrolled
          ? 'bg-[linear-gradient(to_bottom,rgba(255,255,255,0.5),rgba(255,255,255,0.2))] dark:bg-[linear-gradient(to_bottom,rgba(24,24,27,0.5),rgba(24,24,27,0.2))] shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <button 
            onClick={handleLogoClick}
            className="flex items-center group hover:opacity-90 transition-opacity duration-300"
          >
            <img
              src={isDark ? '/assets/logo-dark.svg' : '/assets/logo-light.svg'}
              alt="3D Barbers Logo"
              className="h-[55px] w-auto mr-2 transition-all duration-300 animate-spin-slow"
            />
          </button>

          {/* Desktop Menu */}
          <nav className="hidden md:flex space-x-8">
            <button
              onClick={() => scrollToSection('services')}
              className={getButtonClass('services')}
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('find-us')}
              className={getButtonClass('find-us')}
            >
              Find Us
            </button>
            <button
              onClick={() => scrollToSection('connect')}
              className={`${getButtonClass('connect')} whitespace-nowrap`}
            >
              Connect With Us
            </button>
            <button
              onClick={() => navigate('/booking')}
              className="bg-amber-500 hover:bg-amber-600 text-gray-900 dark:text-gray-900 px-4 py-2 rounded-full transition-colors duration-300 font-medium inline-flex items-center justify-center"
            >
              Book Now
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-800 dark:text-gray-200"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-transparent shadow-lg">
          <div className="px-4 py-3 space-y-3">
            <button
              onClick={() => scrollToSection('services')}
              className={`block ${getButtonClass('services')}`}
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('find-us')}
              className={`block ${getButtonClass('find-us')}`}
            >
              Find Us
            </button>
            <button
              onClick={() => scrollToSection('connect')}
              className={`block ${getButtonClass('connect')} whitespace-nowrap`}
            >
              Connect With Us
            </button>
            <button
              onClick={() => {
                navigate('/booking');
                setIsMobileMenuOpen(false);
              }}
              className="w-full bg-amber-500 hover:bg-amber-600 text-gray-900 dark:text-gray-900 px-4 py-2 rounded-full transition-colors duration-300 inline-flex items-center justify-center"
            >
              Book Now
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;